import { Observable } from 'rxjs';
import { Signal } from '@angular/core';
import { ValidatorFn } from '@angular/forms';

export interface IField {
  key: string;
  label: string;
  placeholder: string;
  type: 'text' | 'number' | 'select' | 'date';
  choices: { label: string; value: any }[];
  default: any;
}

export interface FieldOptions {
  validators?: ValidatorFn[];
  key: string;
  label: string;
  options: any[];
  options$: Observable<any[]>;
  value: any;
  type: string;
  field_type: number;
  choices: string;
  multiple: boolean;
  minimum: number;
  maximum: number;
  disabled: boolean;
  readonly: boolean;
  required: boolean;
  placeholder: string;
  autocomplete: string;
  help: string;
  controlType: any | string;
  order: number;
  rows: number;
  toast?: string | null;
  styleClass?: string;
  updateOn?: 'change' | 'blur' | 'submit';
  searching: Signal<boolean>;
  purposeCodeFilteringCtrl?: any;
  filteredList: Signal<any[]>;
}

export class FormFieldBase<T> {
  validators?: ValidatorFn[];
  value: T;
  key: string;
  type: string;
  // tslint:disable-next-line:variable-name
  field_type: number;
  rows: number;
  options: any[];
  options$: Observable<any[]>;
  label: string;
  choices: string;
  multiple: boolean;
  minimum: number;
  maximum: number;
  disabled: boolean;
  readonly: boolean;
  required: boolean;
  placeholder!: string;
  autocomplete!: string;
  help!: string;
  order: number;
  controlType: string;
  toast?: string | null;
  styleClass?: string;
  updateOn?: 'change' | 'blur' | 'submit';
  searching: Signal<boolean>;
  purposeCodeFilteringCtrl?: any;
  filteredList: Signal<any[]>;

  constructor(options: FieldOptions) {
    this.validators = options.validators;
    this.key = options.key || '';
    this.value = options.value;
    this.type = options.type;
    this.field_type = options.field_type || 2;
    this.rows = options.rows || 2;
    this.options = options.options || [];
    this.options$ = options.options$ || [];
    this.label = options.label || '';
    this.choices = options.choices || '';
    this.multiple = options.multiple || false;
    this.minimum = options.minimum || 0;
    this.maximum = options.maximum || 0;
    this.disabled = options.disabled;
    this.readonly = options.readonly;
    this.required = options.required;
    this.placeholder = options.placeholder || '';
    this.autocomplete = options.autocomplete || 'off';
    this.help = options.help || '';
    this.order = options.order === undefined ? 1 : options.order;
    this.controlType = options.controlType || '';
    this.toast = options.toast || null;
    this.styleClass = options.styleClass || '';
    this.updateOn = options.updateOn || 'change';
    this.searching = options.searching;
    this.purposeCodeFilteringCtrl = options.purposeCodeFilteringCtrl;
    this.filteredList = options.filteredList;
  }

  populate(initialData: FieldOptions): void {
    this.value = initialData.value !== undefined ? initialData.value : null;
  }
}

export class LineInputField extends FormFieldBase<string> {
  controlType = 'lineInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'text';
  }
}

export class LineAmountInputField extends FormFieldBase<string> {
  controlType = 'lineAmountInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'text';
  }
}

export class NumberInputField extends FormFieldBase<number> {
  controlType = 'numberInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'number';
  }
}

export class RangeInputField extends NumberInputField {
  min: number;
  max: number;

  constructor(options: FieldOptions) {
    super(options);
    this.min = options.maximum || 0;
    this.max = options.minimum || 0;
  }
}

export class EmailInputField extends FormFieldBase<string> {
  controlType = 'emailInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'email';
  }
}

export class DateInputField extends FormFieldBase<Date | string> {
  controlType = 'dateInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'date';
  }
}

export class DateTimeInputField extends FormFieldBase<Date | string> {
  controlType = 'dateTimeInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'date';
  }
}

export class FileInputField extends FormFieldBase<Blob> {
  controlType = 'fileInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'file';
  }
}

export class TextboxField extends FormFieldBase<string> {
  controlType = 'multilineInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'text';
  }
}

export class CheckboxField extends FormFieldBase<string> {
  controlType = 'checkboxInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || '';
  }
}

export class LocationInputField extends FormFieldBase<string> {
  controlType = 'locationInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = 'text';
  }
}

export class PasswordInputField extends FormFieldBase<string> {
  controlType = 'passwordInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = 'password';
  }
}

export class HiddenInputField extends FormFieldBase<string> {
  controlType = 'hiddenInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = 'hidden';
  }
}

export class DropdownInputField extends FormFieldBase<string> {
  controlType = 'dropdownInput';
  options: { key: string; value: string }[] = [];

  constructor(options: FieldOptions) {
    super(options);
    this.options = options.options || [];
  }
}

export class AutoCompleteInputField extends FormFieldBase<string> {
  controlType = 'autoCompleteInput';
  options: { key: string; value: string }[] = [];

  constructor(options: FieldOptions) {
    super(options);
    this.options$ = options.options$;
    this.options = options.options || [];
  }
}

export class BICAutoCompleteInputField extends FormFieldBase<string> {
  controlType = 'bicAutoCompleteInput';
  type: string;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'text';
  }
}

export class EquitySearchSelectOptionsInputField extends FormFieldBase<string> {
  controlType = 'equityMatSelectSearchInput';
  type: string;
  searching: Signal<boolean>;
  filteredList: Signal<any[]>;
  purposeCodeFilteringCtrl?: any;

  constructor(options: FieldOptions) {
    super(options);
    this.type = options.type || 'text';
    this.searching = options.searching ?? false;
    this.purposeCodeFilteringCtrl = options?.purposeCodeFilteringCtrl;
    this.filteredList = options?.filteredList;
  }
}

export interface FieldAttribute extends FieldOptions {
  id: any;
  field_type: FIELD_TYPES;
}

export const LINE_FIELD = 1;
export const PARAGRAPH_FIELD = 2;
export const EMAIL_FIELD = 3;
export const BOOLEAN_FIELD = 4;
export const MULTI_BOOLEAN_FIELD = 5;
export const SELECT_FIELD = 6;
export const MULTI_SELECT_FIELD = 7;
export const DATE_FIELD = 10;
export const DATETIME_FIELD = 11;
export const FILE_FIELD = 9;
export const HIDDEN_FIELD = 12;
export const NUMBER_FIELD = 13;
export const URL_FIELD = 14;
export const LOCATION_FIELD = 16;
export const PHONE_NUMBER_FIELD = 17;
export const RANGE_FIELD = 18;
export const AUTOCOMPLETE_FIELD = 19;
export const BIC_AUTOCOMPLETE_FIELD = 20;
export const LINE_AMOUNT_FIELD = 21;
export const EQUITY_SEARCH_SELECT = 22;

export enum FIELD_TYPES {
  LINE = 1,
  PARAGRAPH,
  EMAIL,
  BOOLEAN,
  MULTI_CHECK,
  SELECT,
  MULTI_SELECT,
  MULTI_RADIO,
  FILE,
  DATE,
  DATETIME,
  HIDDEN,
  NUMBER,
  URL,
  DOB,
  LOCATION,
  PHONE_NUMBER,
  RANGE_FIELD,
  AUTOCOMPLETE_FIELD,
  BIC_AUTOCOMPLETE_FIELD,
  LINE_AMOUNT_FIELD,
  EQUITY_SEARCH_SELECT,
}
